import { useState } from 'react';
import { useElementScrollAnimation } from '../../../hooks/useScrollAnimation';

const values = [
  {
    label: '표현',
    icon: 'ri-mic-2-line',
    title: '진심을 담은 표현',
    desc: '연기는 자신을 표현하는 가장 깊은 언어입니다. 찬연기아카데미에서는 테크닉보다 진심을, 완벽함보다 진정성을 먼저 가르칩니다. 그것이 우리 학생들이 오디션장에서 빛나는 이유입니다.',
  },
  {
    label: '창의',
    icon: 'ri-lightbulb-line',
    title: '경계를 넘는 창의력',
    desc: '정해진 틀에 갇히지 않습니다. 학생 각자의 독창적인 시각과 감수성을 자유롭게 펼칠 수 있도록, 즉흥극·창작 워크숍·장르 실험 수업을 통해 창의적 사고와 표현 능력을 함께 키워갑니다.',
  },
  {
    label: '성장',
    icon: 'ri-seedling-line',
    title: '꾸준한 성장의 과정',
    desc: '단번에 완성되는 배우는 없습니다. 입학 첫날부터 수료일까지, 체계적인 커리큘럼과 1:1 피드백을 통해 학생 스스로 자신의 성장을 체감할 수 있도록 단계별 목표를 함께 설계합니다.',
  },
  {
    label: '열정',
    icon: 'ri-fire-line',
    title: '꺼지지 않는 열정',
    desc: '재능보다 중요한 것은 포기하지 않는 열정입니다. 찬연기아카데미는 학생들이 힘든 순간에도 무대를 향한 불꽃을 잃지 않도록, 강사진이 끝까지 옆에서 응원하고 이끌어 갑니다.',
  },
];

const highlights = [
  { icon: 'ri-trophy-line', label: '합격률 100%' },
  { icon: 'ri-user-star-line', label: '전문 강사진' },
  { icon: 'ri-book-open-line', label: '체계적 커리큘럼' },
];

export default function AboutSection() {
  const containerRef = useElementScrollAnimation();
  const [activeIndex, setActiveIndex] = useState(0);

  const active = values[activeIndex];

  return (
    <section id="about" className="py-24 bg-white" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Top: Label + Title */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-16">
          <div className="animate-on-scroll-left">
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-4 block">About Us</span>
            <h2 className="font-serif-kr font-black text-neutral-900 text-4xl md:text-5xl lg:text-6xl leading-tight">
              찬연기아카데미의<br />교육 철학
            </h2>
          </div>
          <div className="md:max-w-sm animate-on-scroll-right animate-delay-200">
            <p className="text-neutral-500 text-sm leading-relaxed">
              찬연기아카데미는 단순히 연기 기술을 가르치는 곳이 아닙니다. 학생 한 명 한 명의 개성과 감수성을 존중하며,
              내면의 이야기를 무대 위에서 진실되게 표현할 수 있도록 이끌어 갑니다.
            </p>
            <p className="text-neutral-500 text-sm leading-relaxed mt-4">
              예고·예대 입시반부터 취미반, 전문배우 양성 과정까지 다양한 프로그램을 통해
              여러분의 꿈에 가장 가까운 길을 함께 걸어갑니다.
            </p>
          </div>
        </div>

        {/* Bottom: Dual Column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Image */}
          <div className="relative animate-on-scroll-left animate-delay-100">
            <div className="w-full h-96 lg:h-[500px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/f2da26bea00708779df4a25d5d2a1c62.jpg"
                alt="찬연기아카데미 스튜디오"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/30 to-transparent" />
            </div>
            {/* Highlights */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {highlights.map((h, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm text-xs font-medium text-white border border-white/20"
                >
                  <i className={`${h.icon} text-red-500`} />
                  {h.label}
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div className="flex flex-col gap-6 animate-on-scroll-right animate-delay-200">
            {/* Values */}
            <div>
              <h3 className="font-serif-kr font-bold text-neutral-900 text-3xl md:text-4xl mb-6">우리의 가치</h3>
              <div className="flex flex-wrap gap-3 mb-6">
                {values.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveIndex(i)}
                    className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                      i === activeIndex
                        ? 'border-2 border-red-500 text-red-500'
                        : 'border border-neutral-300 text-neutral-500 hover:text-neutral-800 hover:border-neutral-400'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Active Value Detail */}
              <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-6 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-500/10">
                    <i className={`${active.icon} text-red-500 text-lg`} />
                  </div>
                  <h4 className="font-serif-kr font-bold text-neutral-900 text-lg">{active.title}</h4>
                </div>
                <p className="text-neutral-500 text-sm leading-relaxed">{active.desc}</p>
              </div>
            </div>

            {/* Light Card */}
            <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-8">
              <div className="flex-1">
                <h4 className="font-serif-kr font-bold text-neutral-900 text-xl mb-3">원장의 인사말</h4>
                <p className="text-neutral-500 text-sm leading-relaxed">
                  "배우를 꿈꾸는 모든 분들을 환영합니다. 입시를 준비하는 학생이든,
                  삶의 새로운 도전을 찾는 어른이든, 이곳에서 여러분은 스스로도 몰랐던
                  자신의 가능성을 발견하게 될 것입니다."
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-8 h-0.5 bg-red-500" />
                  <span className="text-neutral-600 text-xs font-medium">안선애 원장</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
